import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { LiveRosterRow, Objective, SessionFull } from '../api/missions';
import { noticeOf, shownSince, useConnectivity } from '../api/connectivity';
import { ca } from '../i18n/ca';
import { ConnectionNotice, PageLoading } from './PageStates';
import { useOwnConnectionNotice } from './connectionHelpers';
import { useAuth } from './AuthContext';
import { OBJECTIVE_ICONS } from './objectiveIcons';
import { avatarAt } from './avatarAt';
import { MissionCover } from './MissionCover';
import { Planned } from './Planned';
import { SupportPicture, type SupportPick } from './SupportPicture';
import { holdMinutesOf, holdSecondsLeft } from './sessionState';
import type {
  CommandDecision,
  Connection,
  DistanceNow,
  HoldClock,
  PhaseNow,
  Snapshot,
  SupportState,
  TimelineRow,
} from './sessionState';

/**
 * The live session page's own cards (UI doc 5.1), in the order a phone reads
 * them: the header strip, NOW, then (on the page) DECIDE, then SUPPORTS; the
 * roster and the timeline further down. Each one takes what it draws as
 * props, so each is tested on its own and none of them asks the platform for
 * anything.
 */

const ago = (ms: number | null, now: number) =>
  ms === null ? null : ca.sessionNow.ago((now - ms) / 1000);

/**
 * The header strip (UI doc 5.1): the mission's title, the phase pill, the
 * server clock and the in-game hour, the heartbeat, and the avatar or the way
 * in. Sticky on the page (`Shell`'s `header`), 56 px on a phone.
 */
export function SessionHeader({
  title,
  code,
  state,
  outcome,
  phase,
  elapsed,
  inGame,
  connection,
  heartbeat,
}: {
  title: string;
  code: string;
  state: string;
  outcome: string | null;
  /** The phase while the night is live; null before and after. */
  phase: PhaseNow | null;
  /** "1:04:22" since the session started, while it is live. */
  elapsed: string | null;
  /** "21:04" in the game, when the runtime has said enough to count it. */
  inGame: string | null;
  connection: Connection;
  /** "actualitzat fa 4 s", beside the dot. */
  heartbeat: string | null;
}) {
  return (
    <div className="strip-row">
      <div className="strip-title">
        <p className="kicker muted small">
          {ca.session} · {code}
        </p>
        <h1 title={title}>{title}</h1>
      </div>
      <div className="strip-facts">
        {state === 'LIVE' && phase ? (
          <span className="chip phase" title={ca.sessionNow.phaseOf(phase.number, phase.total)}>
            {ca.sessionNow.phase(phase.number, ca.sessionNow.phaseNames[phase.kind])}
          </span>
        ) : (
          <span className="chip">
            {state === 'ENDED' && outcome
              ? (ca.outcomes[outcome] ?? outcome)
              : (ca.sessionStates[state] ?? state)}
          </span>
        )}
        {elapsed && (
          <span className="chip num" title={ca.live.elapsedLabel}>
            <i className="bi bi-stopwatch" aria-hidden="true" />
            <span className="sr-only">{ca.live.elapsedLabel}: </span>
            {elapsed}
          </span>
        )}
        {inGame && (
          <span className="chip num" title={ca.sessionNow.inGameHint}>
            <i className="bi bi-sun" aria-hidden="true" />
            <span className="sr-only">{ca.sessionNow.inGame}: </span>
            {inGame}
          </span>
        )}
        {/* The seconds tick every second, so they are not a live region: a screen
            reader hears the connection change state, and nothing else. */}
        <span
          className={`heartbeat ${connection}`}
          title={heartbeat ?? ca.sessionNow.heartbeat[connection]}
        >
          <span className="dot" aria-hidden="true" />
          <span className="hb-text" aria-hidden="true">
            {connection === 'disconnected' || !heartbeat
              ? ca.sessionNow.heartbeat[connection]
              : heartbeat}
          </span>
          <span className="sr-only" role="status">
            {ca.sessionNow.heartbeat[connection]}
          </span>
        </span>
      </div>
      <StripMe />
    </div>
  );
}

/** The strip's own corner: the account's avatar, or the page's one way into the account. */
function StripMe() {
  const { me } = useAuth();
  const { pathname } = useLocation();
  return (
    <div className="strip-me">
      {me.authenticated ? (
        <Link
          className="avatar"
          to={`/u/${me.handle ?? ''}`}
          aria-label={ca.sessionNow.you}
          title={me.displayName ?? me.handle ?? ''}
        >
          {me.avatarUrl ? (
            <img src={avatarAt(me.avatarUrl, 32)} alt="" width={32} height={32} />
          ) : (
            <span aria-hidden="true">
              {(me.displayName ?? me.handle ?? '?').slice(0, 1).toUpperCase()}
            </span>
          )}
        </Link>
      ) : (
        // The page's one way into the account (the owner's review, E7): it says
        // "the account" because "Entra" beside a live game read as joining it.
        <Link className="button small" to={`/signin?next=${encodeURIComponent(pathname)}`}>
          <i className="bi bi-box-arrow-in-right" aria-hidden="true" />{' '}
          {ca.sessionPage.signInAccount}
        </Link>
      )}
    </div>
  );
}

/**
 * The briefing's strip: the way back to the session and the account's corner.
 * The mission's title is the banner's own heading under it, so the strip
 * carries none.
 */
export function BriefingStrip({ code }: { code: string }) {
  return (
    <div className="strip-row">
      <div className="strip-title">
        <Link className="back" to={`/s/${code}`}>
          <i className="bi bi-arrow-left" aria-hidden="true" /> {ca.sessionPage.backToSession}
        </Link>
      </div>
      <div className="strip-facts">
        <span className="chip">
          {ca.briefing} · {code}
        </span>
      </div>
      <StripMe />
    </div>
  );
}

/**
 * The NOW card (UI doc 5.1, always first): the objective in hand with its
 * task icon, its grid and its state, the night's progress, and the latest
 * line of radio. A tap opens it on the map, with its details in the sheet.
 * The distance from the nearest player and an objective's own hold timer are
 * the runtime's readings (`distance` and `hold` events): the distance says how
 * old its last reading is, the timer counts down from its last report, and
 * both say so plainly until the runtime has sent one (nothing is guessed).
 */
export function NowCard({
  objective,
  title,
  state,
  grid,
  distance = null,
  holdClock = null,
  now = Date.now(),
  progress,
  radio,
  runtimeUp,
  onOpen,
}: {
  objective: Objective | undefined;
  title: string;
  state: string;
  grid: string | null;
  /** The runtime's last reading of the nearest player's distance to this objective. */
  distance?: DistanceNow | null;
  /** The hold objective's clock as last reported. */
  holdClock?: HoldClock | null;
  /** The page's clock in ms, so the countdown and the age move. */
  now?: number;
  progress: { resolved: number; total: number };
  radio: string | null;
  /** Whether the runtime has reported anything: before that the server is still loading. */
  runtimeUp: boolean;
  onOpen?: (id: string) => void;
}) {
  const pct = progress.total ? Math.round((progress.resolved / progress.total) * 100) : 0;
  const holdMinutes = objective && state === 'active' ? holdMinutesOf(objective) : null;
  const empty = !runtimeUp
    ? ca.live.startingHint
    : progress.total > 0 && progress.resolved === progress.total
      ? ca.sessionNow.allResolved
      : ca.live.noObjective;
  return (
    <article className="card now" aria-label={ca.live.now}>
      <h2>{ca.live.now}</h2>
      {!objective ? (
        <p className="muted">{empty}</p>
      ) : (
        <>
          <button
            type="button"
            className="now-main"
            onClick={() => onOpen?.(objective.id)}
            title={ca.sessionNow.details}
          >
            <i
              className={`bi bi-${OBJECTIVE_ICONS[objective.type] ?? 'geo-alt'}`}
              aria-hidden="true"
            />
            <span className="now-title">{title}</span>
            <i className="bi bi-chevron-right muted" aria-hidden="true" />
          </button>
          <p className="row">
            <span className="chip">{ca.priorityNames[objective.priority ?? 'main']}</span>
            <span className={`chip ${state === 'active' ? 'warn' : ''}`}>
              {ca.objectiveUi.states[state] ?? state}
            </span>
            {grid && (
              <span className="chip num" title={ca.sessionNow.gridHint}>
                {ca.sessionNow.grid} {grid}
              </span>
            )}
          </p>
        </>
      )}
      {progress.total > 0 && runtimeUp && (
        <div className="progress-line">
          <span
            className="progressbar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.resolved}
            aria-label={ca.sessionNow.progress(progress.resolved, progress.total)}
          >
            <span style={{ width: `${pct}%` }} />
          </span>
          <span className="muted small">
            {ca.sessionNow.progress(progress.resolved, progress.total)}
          </span>
        </div>
      )}
      {radio && (
        <p className="radio-line" aria-label={ca.sessionNow.radio}>
          <i className="bi bi-broadcast" aria-hidden="true" /> {radio}
        </p>
      )}
      {objective && (
        <dl className="now-facts">
          {distance ? (
            <div className="fact">
              <dt title={ca.sessionNow.distanceHint}>{ca.sessionNow.distance}</dt>
              <dd>
                <span className="num">{ca.sessionNow.distanceValue(distance.metres)}</span>{' '}
                <span className="muted small">{ago(distance.receivedAt, now)}</span>
              </dd>
            </div>
          ) : (
            <NotYet term={ca.sessionNow.distance} why={ca.sessionNow.distanceWhy} />
          )}
          {holdMinutes !== null && (
            <>
              <div className="fact">
                <dt>{ca.sessionNow.holdTotal(holdMinutes)}</dt>
              </div>
              {holdClock ? (
                <div className="fact">
                  <dt>{ca.sessionNow.holdLeft}</dt>
                  <dd>
                    <span className="num">
                      {ca.sessionNow.holdClock(holdSecondsLeft(holdClock, now))}
                    </span>
                    {holdClock.state === 'paused' && (
                      <>
                        {' '}
                        <span className="chip" title={ca.sessionNow.holdPausedHint}>
                          {ca.sessionNow.holdPaused}
                        </span>
                      </>
                    )}
                  </dd>
                </div>
              ) : (
                <NotYet
                  term={ca.sessionNow.holdLeft}
                  why={ca.sessionNow.holdLeftWhy}
                  notYet={ca.sessionNow.holdNotStarted}
                />
              )}
            </>
          )}
        </dl>
      )}
    </article>
  );
}

/**
 * A fact the page is built for and the runtime has not sent yet: its name, «encara
 * no disponible» (or the words for this fact), the sentence saying what is missing
 * and, for a feature still planned, the «Pendent» chip. Nothing is guessed in its
 * place.
 */
function NotYet({
  term,
  why,
  chip,
  notYet,
}: {
  term: string;
  why: string;
  chip?: ReactNode;
  notYet?: string;
}) {
  return (
    <div className="fact missing">
      <dt>{term}</dt>
      <dd>
        <span className="muted">{notYet ?? ca.sessionNow.notYet}</span> {chip}
        <span className="muted small block">{why}</span>
      </dd>
    </div>
  );
}

/**
 * The SUPPORTS card (UI doc 5.1): whether each support is ready or in its
 * cooldown, as read-only pills, since they are called from inside the game and
 * not from the phone, and the latest decision from command, because that is
 * news the whole side wants. Drawn only when the mission has supports or
 * command has decided something.
 */
export function SupportsCard({
  rows,
  fromSnapshot,
  snapshotAt,
  decision,
  now,
  picks,
  modSetHash,
}: {
  rows: SupportState[];
  /** What each support is made of, for its picture in its pill (the session view's `supports`). */
  picks?: SupportPick[];
  modSetHash?: string | null;
  fromSnapshot: boolean;
  snapshotAt: number | null;
  decision: CommandDecision | null;
  now: number;
}) {
  if (rows.length === 0 && !decision) return null;
  return (
    <article className="card supports" aria-label={ca.live.supports}>
      <h2>{ca.live.supports}</h2>
      {rows.length > 0 && (
        <>
          <ul className="pills">
            {rows.map((r) => (
              <li
                key={r.key}
                className={`pill ${r.state === 'cooling' ? 'spent' : ''}`}
                title={r.state === 'unlimited' ? ca.sessionNow.noLimitHint : undefined}
              >
                <SupportPicture
                  compact
                  kind={r.key}
                  assetClass={picks?.find((p) => p.kind === r.key)?.className}
                  modSetHash={modSetHash}
                />
                <span>{ca.supportNames[r.key] ?? r.key}</span>
                <strong className="num">
                  {r.state === 'unlimited'
                    ? ca.sessionNow.noLimit
                    : r.state === 'cooling' && r.readyAt !== null
                      ? ca.sessionNow.coolingFor(Math.max(1, Math.ceil((r.readyAt - now) / 1000)))
                      : ca.sessionNow.ready}
                </strong>
              </li>
            ))}
          </ul>
          <p className="muted small">
            {fromSnapshot
              ? ca.sessionNow.fromSnapshot(ago(snapshotAt, now) ?? '')
              : ca.sessionNow.fromDefinition}{' '}
            {ca.sessionNow.supportsHint}
          </p>
        </>
      )}
      <div className="decision">
        <h3>{ca.sessionNow.commandDecision}</h3>
        {decision ? (
          <p>
            {decision.text}{' '}
            {decision.receivedAt !== null && (
              <span className="muted small">{ago(decision.receivedAt, now)}</span>
            )}
          </p>
        ) : (
          <p className="muted small">{ca.sessionNow.noCommandDecision}</p>
        )}
      </div>
    </article>
  );
}

/**
 * The roster, as far as the runtime tells it: the commander of the night, who
 * is on the server and whether alive by its last snapshot, and how many read
 * the briefing. The in-game names are for somebody signed in (`names`), the
 * page's own rule for anything with names in it on a page public by link; a
 * visitor gets the count and the commander. Squads, avatars and who is down or
 * respawning wait for the panel's live match endpoint (roadmap R3.3), and say so.
 */
export function RosterCard({
  commander,
  snapshot,
  live = [],
  players,
  briefingReads,
  now,
  names,
}: {
  commander: { handle: string; displayName: string } | null;
  snapshot: Snapshot | null;
  /** Who the panel says is connected now; empty when it cannot say, and the snapshot's list stands. */
  live?: LiveRosterRow[];
  /** Players on the server by the last health report. */
  players: number | null;
  briefingReads: number;
  now: number;
  /** Whether the reader may see in-game names: signed in. */
  names: boolean;
}) {
  const list = snapshot?.players ?? [];
  return (
    <article className="card roster" id="roster" aria-label={ca.sessionNow.roster}>
      <h2>{ca.sessionNow.roster}</h2>
      <p className="row">
        {commander ? (
          <span className="chip on">
            <i className="bi bi-star-fill" aria-hidden="true" />{' '}
            {ca.live.commander(commander.displayName)}
          </span>
        ) : (
          <span className="muted small">{ca.live.commanderNone}</span>
        )}
        {players !== null && (
          <span className="chip num">{ca.sessionNow.rosterOnServer(players)}</span>
        )}
      </p>
      {!names ? null : live.length > 0 ? (
        <>
          <p className="muted small num">{ca.sessionNow.rosterConnected(live.length)}</p>
          <ul className="plain roster-list">
            {live.map((p, i) => {
              const side = ca.sessionNow.rosterSide(p.side);
              const alive = list.find((s) => s.name === p.name)?.alive;
              return (
                <li key={`${p.name}-${i}`} className={alive === false ? 'dead' : ''}>
                  <span className="grow">{p.displayName ?? p.name}</span>
                  {side && <span className="chip">{side}</span>}
                  {alive !== undefined && (
                    <span className={`chip ${alive ? 'ok' : 'bad'}`}>
                      {alive ? ca.sessionNow.alive : ca.sessionNow.dead}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      ) : list.length > 0 ? (
        <>
          <p className="muted small num">
            {ca.sessionNow.rosterCounts(
              list.filter((p) => p.alive).length,
              list.filter((p) => !p.alive).length,
            )}
          </p>
          <ul className="plain roster-list">
            {list.map((p, i) => (
              <li key={`${p.name}-${i}`} className={p.alive ? '' : 'dead'}>
                <span className="grow">{p.name}</span>
                <span className={`chip ${p.alive ? 'ok' : 'bad'}`}>
                  {p.alive ? ca.sessionNow.alive : ca.sessionNow.dead}
                </span>
              </li>
            ))}
          </ul>
          <p className="muted small">
            {ca.sessionNow.rosterAsOf(ago(snapshot?.receivedAt ?? null, now) ?? '')}
          </p>
          <dl className="now-facts">
            <NotYet term={ca.sessionNow.rosterRole} why={ca.sessionNow.rosterRoleWhy} />
            <NotYet term={ca.sessionNow.rosterPositions} why={ca.sessionNow.rosterPositionsWhy} />
          </dl>
        </>
      ) : (
        <p className="muted small">{ca.sessionNow.rosterNone}</p>
      )}
      <p className="muted small">{ca.briefingReads(briefingReads)}</p>
      <Planned ids={['roster-squads', 'roster-wounded', 'roster-positions']} />
    </article>
  );
}

const minuteOf = (r: TimelineRow) =>
  r.minute === null
    ? '·'
    : r.minuteTo !== undefined
      ? `${r.minute}–${r.minuteTo}′`
      : `${r.minute}′`;

function TimelineLine({ row }: { row: TimelineRow }) {
  return (
    <>
      <span className="muted small num when">{minuteOf(row)}</span>
      <i className={`bi bi-${row.icon}`} aria-hidden="true" />
      {/* The game's own class, for a tester's or an admin's pointer and never as text. */}
      <span className="grow" title={row.hint}>
        {row.text}
      </span>
    </>
  );
}

/**
 * A row that stands for several (casualties of one side in a row): one line,
 * closed, with a button that opens the men it stands for, oldest first. The
 * lines are not in the page until it is opened (the owner, test card 172,
 * 2026-10-17: the fold read as its count followed by every line it folds).
 */
function FoldedRow({ row }: { row: TimelineRow }) {
  const [open, setOpen] = useState(false);
  const t = ca.sessionTimeline;
  return (
    <li className="folded">
      <button
        type="button"
        className="fold-toggle"
        aria-expanded={open}
        title={open ? t.close : t.open}
        onClick={() => setOpen((o) => !o)}
      >
        <TimelineLine row={row} />
      </button>
      {open && (
        <ol className="plain timeline-list">
          {(row.items ?? []).map((item) => (
            <li key={item.key} className={item.tone ?? ''}>
              <TimelineLine row={item} />
            </li>
          ))}
        </ol>
      )}
    </li>
  );
}

/**
 * The timeline (UI doc 5.1): newest first, each row a sentence with its minute
 * and an icon. A row that stands for several opens on them (`FoldedRow`).
 */
export function SessionTimeline({
  rows,
  title = ca.timeline,
  empty = ca.noEventsYet,
}: {
  rows: TimelineRow[];
  title?: string;
  empty?: string;
}) {
  return (
    <section className="card timeline" aria-label={title}>
      <h2>{title}</h2>
      {rows.length === 0 && <p className="muted small">{empty}</p>}
      <ol className="plain timeline-list">
        {rows.map((r) =>
          r.items ? (
            <FoldedRow key={r.key} row={r} />
          ) : (
            <li key={r.key} className={r.tone ?? ''}>
              <TimelineLine row={r} />
            </li>
          ),
        )}
      </ol>
    </section>
  );
}

/**
 * The page's banner, before the night and after it (the owner, 2026-10-12):
 * the mission's own picture behind one large line, which is the countdown while
 * the session waits and the result once it is over, then the facts as chips and
 * whatever the page puts under them (the two sides, the buttons). The picture
 * is the same one every list draws the mission with (`MissionCover`), so a
 * mission that gets a cover of its own gets it here too.
 */
export function SessionBanner({
  cover,
  world,
  kicker,
  lead,
  tone,
  facts,
  label = ca.sessionPage.banner,
  heading = false,
  children,
}: {
  /** What the banner is, for whoever does not see it. */
  label?: string;
  /** The large line is the page's own heading (the briefing, which has no strip title). */
  heading?: boolean;
  cover: SessionFull['cover'];
  /** The terrain's class, for a platform that does not send `cover` yet. */
  world: string;
  /** A small line over the large one: what the large one is. */
  kicker?: ReactNode;
  /** The one large line: "Comença en 2 d 4 h", "Èxit", the mission's title on the briefing. */
  lead: ReactNode;
  tone?: 'ok' | 'bad';
  facts?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="session-banner" aria-label={label}>
      <MissionCover
        world={cover?.worldName ?? world}
        worldLabel={cover?.worldDisplayName}
        recipe={cover?.recipe}
        recipeIcon={cover?.recipeIcon}
        recipeTitle={cover?.recipeTitle}
        cover={cover?.cover}
        className="session-banner-bg"
        size="card"
      />
      <div className="session-banner-body">
        {kicker && <p className="kicker muted small">{kicker}</p>}
        {heading ? (
          <h1 className={`banner-lead ${tone ?? ''}`}>{lead}</h1>
        ) : (
          <p className={`banner-lead ${tone ?? ''}`}>{lead}</p>
        )}
        {facts && <div className="row chips banner-facts">{facts}</div>}
        {children}
      </div>
    </section>
  );
}

/** One figure of a night that is over: a number over what it counts. */
export function Figure({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="figure">
      <dt>{label}</dt>
      <dd className="num">{value}</dd>
    </div>
  );
}

/** One person at a night that is over, as the card draws them. */
export interface Person {
  name: string;
  /** The account's handle when there is one: the name then links to the profile. */
  handle?: string | null;
  /** The Steam or Discord picture of the account; absent draws the initial. */
  avatarUrl?: string | null;
}

/**
 * Who was there: the picture of each person's Steam or Discord account when
 * the platform has one, else a round initial, and the name, the way the header
 * draws an account. The list is the platform's own (`/attendees`: the panel's
 * roster and the runtime's snapshots, once each, never a headless client); when
 * it has none the server's last snapshot names stand in, without pictures. The
 * names are for somebody signed in, the page's rule for anything with names in
 * it.
 */
export function WhoWasThere({ people, hint }: { people: Person[]; hint?: string }) {
  if (people.length === 0) return null;
  return (
    <article className="card who" aria-label={ca.sessionPage.whoWasThere}>
      <h2>{ca.sessionPage.whoWasThere}</h2>
      <ul className="plain who-list">
        {people.map((p, i) => {
          const name = p.name.trim() || ca.sessionPage.attendeeUnnamed;
          const face = p.avatarUrl ? (
            <img
              className="avatar"
              src={avatarAt(p.avatarUrl, 28)}
              alt=""
              width={28}
              height={28}
              loading="lazy"
            />
          ) : (
            <span className="avatar" aria-hidden="true">
              {name.slice(0, 1).toUpperCase()}
            </span>
          );
          return (
            <li key={`${p.handle ?? name}-${i}`}>
              {p.handle ? (
                <Link to={`/u/${p.handle}`}>
                  {face}
                  <span>{name}</span>
                </Link>
              ) : (
                <>
                  {face}
                  <span>{name}</span>
                </>
              )}
            </li>
          );
        })}
      </ul>
      <p className="muted small">{hint ?? ca.sessionPage.whoWasThereHint}</p>
    </article>
  );
}

/**
 * The page's word on its own connection (UI doc 7): while the platform cannot
 * be reached, the shared band (`ConnectionNotice`) above the last known state,
 * never a spinner over it, with a way to try again. A stale page needs no
 * banner: the header's grey heartbeat already says how old the news is. The
 * page reads its own stream, so the shell's notice stays hidden beside it.
 */
export function ConnectionBanner({
  connection,
  online,
  onRetry,
  since = null,
}: {
  connection: Connection;
  online: boolean;
  onRetry: () => void;
  /** When the last news arrived; the page's heartbeat knows it. */
  since?: number | null;
}) {
  useOwnConnectionNotice();
  // The page's own reading first; past it, what `apiFetch` heard: an answer the
  // service worker served from its copy reads as success to the page's hook.
  const c = useConnectivity();
  const kind = connection === 'disconnected' ? (online ? 'unreachable' : 'offline') : noticeOf(c);
  return (
    <ConnectionNotice
      kind={kind}
      since={since ?? shownSince(c)}
      hint={ca.sessionNow.disconnectedHint}
      onRetry={onRetry}
    />
  );
}

/** The first paint while the session loads (UI doc 7): the cards' shapes, not a spinner. */
export function SessionSkeleton() {
  return <PageLoading label={ca.sessionNow.loading} />;
}
